import type { AppUsageReport, LatestRelease, WorthACallRow } from '@/features/reports/api';
import { APP_USAGE_COPY as COPY } from '@/features/reports/copy';
import {
    APP_USAGE_REASON,
    type AppUsageReason,
    VERSION_STANDING,
    type VersionStanding,
} from '@/features/reports/tabs';
import { formatDayMonth } from '@/lib/format';

/*
  Reports › App usage's words for what the API counted (ALS-160). The API sends each figure and
  reason; these put them into the prototype's sentences. Dates are read as the calendar day they
  name, since the log's times are the database's wall clock and "yesterday" is a day, not 24 hours.
*/

type BadgeTone = 'success' | 'warning' | 'secondary' | 'destructive';

/** The version badge: green on the latest, amber two or more behind, neutral otherwise. */
export function standingVariant(standing: VersionStanding): BadgeTone {
    return standing === VERSION_STANDING.Latest
        ? 'success'
        : standing === VERSION_STANDING.WellBehind
          ? 'warning'
          : 'secondary';
}

/** Worth a call's tag colour per reason, as the prototype paints them. */
export const REASON_VARIANT: Record<AppUsageReason, BadgeTone> = {
    [APP_USAGE_REASON.ResetPassword]: 'secondary',
    [APP_USAGE_REASON.ReleasesBehind]: 'warning',
    [APP_USAGE_REASON.NotOpened]: 'destructive',
};

/** The calendar day an API date or date-time names, in local time. */
export function calendarDay(value: string): Date {
    const [year, month, day] = value.slice(0, 10).split('-').map(Number);
    return new Date(year, month - 1, day);
}

const DAY = 24 * 60 * 60 * 1000;

/** "today", "yesterday", "5 days ago", counted in days back from the window's last. */
export function ago(at: string, today: string): string {
    return COPY.ago(Math.round((calendarDay(today).getTime() - calendarDay(at).getTime()) / DAY));
}

/** "24 Aug". */
export function dayMonth(at: string): string {
    return formatDayMonth(calendarDay(at));
}

/**
 * The version "the latest" names: one when every listed platform's latest is the same release, as
 * the prototype assumes, and "the latest release" when iOS and Android differ. Null with no list.
 */
export function latestVersion(releases: LatestRelease[]): string | null {
    const versions = [...new Set(releases.map((r) => r.version))];
    return versions.length === 1 ? versions[0] : null;
}

/** The latest release on one platform, for a reason that names how far behind it someone is. */
export function latestOn(releases: LatestRelease[], platform: string | null): string | null {
    return releases.find((r) => r.platform === platform)?.version ?? latestVersion(releases);
}

/**
 * The tab's hint: "14 Feb to 15 Mar 2061 · 6 sessions · latest release 4.8.1 (12 Jan)", then the
 * manager when one is chosen. Each platform's release is named when the two differ.
 */
export function windowHint(report: AppUsageReport, manager?: string): string {
    const to = calendarDay(report.to);
    const parts = [
        `${COPY.hint.range(dayMonth(report.from), dayMonth(report.to))} ${to.getFullYear()}`,
        COPY.hint.sessions(report.sessions),
    ];
    const releases = report.latestReleases;
    const single = latestVersion(releases);
    if (single !== null) {
        const newest = releases
            .map((r) => r.releasedOn)
            .sort()
            .at(-1)!;
        parts.push(COPY.hint.release(single, dayMonth(newest)));
    } else if (releases.length > 0) {
        parts.push(
            COPY.hint.releases(
                releases
                    .map((r) => `${r.platform} ${r.version} (${dayMonth(r.releasedOn)})`)
                    .join(', '),
            ),
        );
    }
    if (manager) parts.push(COPY.hint.rm(manager));
    return parts.join(' · ');
}

/** The tag beside a name in Worth a call. */
export function reasonTag(row: WorthACallRow, days: number): string {
    switch (row.reason) {
        case APP_USAGE_REASON.ResetPassword:
            return COPY.calls.tags.ResetPassword();
        case APP_USAGE_REASON.ReleasesBehind:
            return COPY.calls.tags.ReleasesBehind(row.behind ?? 0);
        case APP_USAGE_REASON.NotOpened:
            return COPY.calls.tags.NotOpened(days);
    }
}

/** The reason under the name: what happened, on what, and what the call is for. */
export function reasonLine(row: WorthACallRow, report: AppUsageReport): string {
    const version = row.version ?? COPY.versions.unknown;
    switch (row.reason) {
        case APP_USAGE_REASON.ResetPassword:
            return COPY.calls.why.reset(row.at ? ago(row.at, report.to) : '', row.platform);
        case APP_USAGE_REASON.ReleasesBehind:
            return COPY.calls.why.behind(
                version,
                latestOn(report.latestReleases, row.platform) ?? version,
            );
        case APP_USAGE_REASON.NotOpened:
            return COPY.calls.why.notOpened(row.at ? dayMonth(row.at) : null);
    }
}

/** "Zeina, Rania" or "nobody". */
export function firstNames(names: string[]): string {
    return names.length > 0 ? names.join(', ') : COPY.figures.wellBehind.nobody;
}
